import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface NotificationItem {
  id: string;
  type: string;
  channel: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  status: 'pending' | 'sent' | 'read' | 'failed';
  readAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface NotificationsListResponse {
  data: NotificationItem[];
  meta: { page: number; limit: number; total: number };
}

export interface AlertRule {
  id: string;
  type: string;
  enabled: boolean;
  daysBeforeExpiry: number;
  channels: string;
  recipients: string;
}

const KEY = {
  list: (p: Record<string, unknown>) => ['notifications', p] as const,
  unread: ['notifications', 'unread-count'] as const,
  rules: ['alert-rules'] as const,
};

export function useNotifications(params: { page?: number; limit?: number; status?: string } = {}) {
  return useQuery<NotificationsListResponse>({
    queryKey: KEY.list(params),
    queryFn: async () => {
      const { data } = await api.get<NotificationsListResponse>('/notifications', { params });
      return data;
    },
  });
}

export function useUnreadCount() {
  return useQuery<{ data: { count: number } }>({
    queryKey: KEY.unread,
    refetchInterval: 30_000, // Poll every 30s
    queryFn: async () => {
      const { data } = await api.get<{ data: { count: number } }>('/notifications/unread-count');
      return data;
    },
  });
}

export function useMarkAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/notifications/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/notifications/read-all');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useAlertRules() {
  return useQuery<{ data: AlertRule[] }>({
    queryKey: KEY.rules,
    queryFn: async () => {
      const { data } = await api.get<{ data: AlertRule[] }>('/notifications/alert-rules');
      return data;
    },
  });
}

export function useCreateAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      type: string;
      enabled?: boolean;
      daysBeforeExpiry?: number;
      channels?: string;
      recipients?: string;
    }) => {
      const { data } = await api.post<{ data: AlertRule }>('/notifications/alert-rules', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.rules }),
  });
}

export function useUpdateAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: {
      id: string;
      enabled?: boolean;
      daysBeforeExpiry?: number;
      channels?: string;
      recipients?: string;
    }) => {
      const { data } = await api.patch<{ data: AlertRule }>(
        `/notifications/alert-rules/${id}`,
        patch,
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.rules }),
  });
}

export function useDeleteAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/notifications/alert-rules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.rules }),
  });
}

export function useCheckAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ data: Record<string, number> }>('/notifications/check-alerts');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  cnh_expiring: 'CNH vencendo',
  crlv_expiring: 'CRLV vencendo',
  maintenance_overdue: 'Manutenção atrasada',
  maintenance_upcoming: 'Manutenção próxima',
  checklist_nok: 'Checklist com NOK',
  fueling_anomaly: 'Anomalia de abastecimento',
  invitation: 'Convite',
  system: 'Sistema',
};

export const NOTIFICATION_TYPE_ICON: Record<string, string> = {
  cnh_expiring: 'id-card',
  crlv_expiring: 'file-warning',
  maintenance_overdue: 'alert-triangle',
  maintenance_upcoming: 'calendar-clock',
  checklist_nok: 'clipboard-x',
  fueling_anomaly: 'fuel',
  system: 'info',
};
